import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import AddressGroupV2 from 'components/field/AddressGroupV2';
import AutoComplete from 'components/field/AutoComplete';
import PhoneNumber from 'components/field/PhoneNumber';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';
import Accordion from 'components/global/Accordion';

import useLocale from 'hooks/useLocale';

import { AUTOCOMPLETE } from 'services/consts';

import * as S from '../OrderForm.styled';

const defaultColSpan = {
	md: 6
};

const contactDetailsErrorFields = ['firstName', 'lastName', 'phone', 'email'];
const addressDetailsErrorFields = ['address'];

export const CustomerDetails = ({ formMode, isAdmin }) => {
	const userType = useSelector((state) => state.user.userType);
	const contactDetailsAccordionRef = useRef(null);
	const addressDetailsAccordionRef = useRef(null);
	const { Country } = useLocale();
	const user = useSelector((state) => state.user);

	const formMethods = useFormContext();

	const {
		setValue,
		watch,
		formState: { isLoading: isFormLoading, isSubmitting, errors }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	const autoFillCustomerDetailsLabels = useMemo(() => ['firstName'], []);
	const autoFillCustomerDetailsColumns = useMemo(
		() => [
			{ accessor: 'firstName', width: 150 },
			{ accessor: 'lastName', width: 150 },
			{ accessor: 'phone', width: 150 }
		],
		[]
	);

	const setMultipleValues = useCallback(
		(values) => {
			Object.entries(values).forEach(([key, value]) => {
				setValue(key, value, { shouldValidate: true, shouldDirty: true });
			});
		},
		[setValue]
	);

	useEffect(() => {
		contactDetailsErrorFields.some((field) => {
			if (errors[field]) {
				contactDetailsAccordionRef.current.open();
				return true;
			}
		});
		addressDetailsErrorFields.some((field) => {
			if (errors[field]) {
				addressDetailsAccordionRef.current.open();
				return true;
			}
		});
	}, [errors]);

	return (
		<S.AccordionContainer>
			<Accordion ref={contactDetailsAccordionRef} header="contactDetails" defaultVisible>
				<FormItem colSpan={defaultColSpan} label="firstName" isLoading={isLoading} required>
					{formMode === 'add' ? (
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.AUTOFILL_CUSTOMER_DETAILS}
							name="firstName"
							value={watch('autofillID')}
							setValue={(value) => {
								if (value === null) {
									setMultipleValues({
										autofillID: null,
										firstName: null
									});
									return;
								}
								if (watch('autofillID') !== value?.ID) {
									setMultipleValues({
										autofillID: value?.ID ?? null,
										firstName: value?.firstName ?? null,
										lastName: value?.lastName ?? null,
										phone: value?.phone ?? null,
										email: value?.email ?? null,
										'address.text': value?.text ?? null,
										'address.shortAddressCode': value?.shortAddressCode ?? null,
										'address.countryName': value?.countryName ?? Country,
										'address.cityName': value?.cityName ?? null,
										'address.stateName': value?.stateName ?? null,
										'address.districtName': value?.districtName ?? null,
										'address.neighbourhoodName': value?.neighbourhoodName ?? null,
										'address.buildingName': value?.buildingName ?? null,
										'address.addressInputType': value?.addressInputType ?? 'fullTextAddress',
										'address.street': value?.street ?? null,
										'address.lat': value?.lat ?? null,
										'address.lng': value?.lng ?? null,
										'address.zipCode': value?.zipCode ?? null
									});
								}
							}}
							textValue={watch('firstName')}
							setTextValue={(value) => {
								setMultipleValues({
									autofillID: null,
									firstName: value
								});
							}}
							columns={autoFillCustomerDetailsColumns}
							selectionLabelAccessor={autoFillCustomerDetailsLabels}
							disabled={
								formMode === 'view' ||
								(!isAdmin && userType === 'customerServiceUser' && formMode === 'edit')
							}
							fixedContainerWidth={false}
						/>
					) : (
						<StringInput
							name="firstName"
							disabled={
								formMode === 'view' ||
								(!isAdmin && userType === 'customerServiceUser' && formMode === 'edit')
							}
						/>
					)}
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="lastName" isLoading={isLoading}>
					<StringInput
						disabled={
							formMode === 'view' ||
							(!isAdmin && userType === 'customerServiceUser' && formMode === 'edit')
						}
						name="lastName"
					/>
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="phoneNumber" isLoading={isLoading} required>
					<PhoneNumber
						disabled={
							formMode === 'view' ||
							(!isAdmin && userType === 'customerServiceUser' && formMode === 'edit')
						}
						name="phone"
						value={watch('phone')}
						onChange={(e) => {
							setValue('phone', e, { shouldValidate: true, shouldDirty: true });
						}}
					/>
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="email" isLoading={isLoading}>
					<StringInput
						disabled={
							formMode === 'view' ||
							(!isAdmin && userType === 'customerServiceUser' && formMode === 'edit')
						}
						name="email"
					/>
				</FormItem>
			</Accordion>
			<Accordion ref={addressDetailsAccordionRef} header="addressDetails" defaultVisible>
				<AddressGroupV2
					formFieldName="address"
					disabled={formMode === 'view'}
					isLoading={isLoading}
					formMode={formMode}
					autoFillID={watch('autofillID')}
					requiredFields={{ city: !user.generalSettings?.dataCorrectorIgnoreMissingCity }}
				/>
			</Accordion>
		</S.AccordionContainer>
	);
};
